import { relDay, monthName, fmt, dayLabel } from './format';
import { seriesKeyOf, legacySeriesKeyOf } from '../lib/forecasting/series-key';
import { nextOccurrence, expandRecurring } from '../lib/forecasting/recurrence';
import { activePlans, plannedKeyOf, plannedNext } from '../lib/forecasting/planned';
import { formatMerchantName } from '../lib/merchant';
import { firebaseSafeKey } from '../lib/firebaseKey';
import { isSpendingType, isMoneyIn, signedAmount, spendDelta } from '../lib/txType';

const round2 = (n) => parseFloat(n.toFixed(2));

export const toKey = (cat) => cat.replace(/[^a-zA-Z0-9]/g, '_');

// Bank-provided account names often already end in the masked last four
// (e.g. "Costco Anywhere Visa Card by Citi-3496"); skip re-appending lastFour
// in that case so it doesn't print twice.
export function accountMaskSuffix(name, lastFour, separator = ' ·· ') {
  if (!lastFour) return '';
  const trailingDigits = (String(name || '').match(/(\d{4,})\D*$/) || [])[1];
  if (trailingDigits && trailingDigits.slice(-4) === lastFour) return '';
  return `${separator}${lastFour}`;
}

/**
 * Bank balances are stored as { ledger, available }; legacy may be a number.
 * Cash accounts show `available` — what's actually spendable, with pending
 * holds netted out — falling back to `ledger` for manual accounts, which only
 * store a ledger value. Credit accounts stay on `ledger`: their `available`
 * is the remaining credit line, not an amount owed.
 */
export function accountDisplayBalance(account) {
  const b = account?.balance;
  if (b == null) return 0;
  if (typeof b !== 'object') return Number(b) || 0;
  if (account.type !== 'credit' && b.available != null && !isNaN(Number(b.available))) {
    return Number(b.available);
  }
  return Number(b.ledger) || 0;
}

export const toDisplayTx = (tx) => ({
  id: tx.id,
  desc: formatMerchantName(tx.description) || tx.description,
  cat: tx.category,
  logoUrl: tx.logoUrl || null,
  website: tx.website || null,
  amt: tx.amount,
  type: tx.type,
  date: new Date(tx.date),
  recurring: tx.isRecurring,
  recurrenceType: tx.recurrenceType,
  pending: !!tx.pending,
  raw: tx,
});

export function groupTxByDay(list, today = new Date()) {
  const groups = {};
  list.forEach((t) => {
    const d = t.date instanceof Date ? t.date : new Date(t.date);
    const key = d.toDateString();
    (groups[key] = groups[key] || []).push(t);
  });
  return Object.entries(groups)
    .map(([key, items]) => ({
      key,
      date: new Date(key),
      label: relDay(new Date(key), today),
      total: items.reduce((s, t) => s + signedAmount(t), 0),
      items,
    }))
    .sort((a, b) => b.date - a.date);
}

// Net spend per category: purchases add, refunds give back. A returned item
// has to release the budget it consumed, or the envelope stays spent on
// something the user no longer owns.
export function buildSpendByCat(transactions) {
  const m = {};
  transactions
    .filter((t) => isSpendingType(t.type))
    .forEach((t) => {
      const cat = t.category || t.cat;
      m[cat] = (m[cat] || 0) + spendDelta(t);
    });
  return Object.entries(m)
    .map(([cat, amt]) => ({ cat, amt }))
    .sort((a, b) => b.amt - a.amt);
}

export function buildBudgetRows(
  envelopes,
  categories,
  monthlyTransactions,
  options = {},
) {
  const { envelopeMeta = {}, priorMonthTransactions = [] } = options;
  const spend = buildSpendByCat(monthlyTransactions);
  const priorSpend = buildSpendByCat(priorMonthTransactions);
  // One row per category, in the given category order. `hasLimit` distinguishes
  // a budgeted category (an envelope exists, even if the limit is 0) from an
  // unbudgeted one, so callers can present or filter them however they need:
  // the dashboard keeps only funded budgets, the budget page shows all.
  return categories.map((cat) => {
    const key = toKey(cat);
    const hasLimit = envelopes[key] !== undefined;
    const limit = envelopes[key] ?? 0;
    const spent = spend.find((s) => s.cat === cat)?.amt || 0;
    const rolloverEnabled = !!envelopeMeta[key]?.rollover;
    const priorSpent = priorSpend.find((s) => s.cat === cat)?.amt || 0;
    const rolloverBonus = rolloverEnabled ? Math.max(0, limit - priorSpent) : 0;
    const effectiveLimit = limit + rolloverBonus;
    const pct = effectiveLimit > 0 ? Math.min((spent / effectiveLimit) * 100, 100) : 0;
    const over = effectiveLimit > 0 && spent > effectiveLimit;
    const overage = over ? spent - effectiveLimit : 0;
    // Once spending passes the limit the bar rescales to total spend, so the
    // track splits into "up to the limit" and "the overage". Without this every
    // over-budget row renders the same full bar, whether it is 1% or 200% over.
    const limitPct = over ? (effectiveLimit / spent) * 100 : pct;
    const overPct = over ? 100 - limitPct : 0;
    return {
      cat,
      hasLimit,
      limit,
      effectiveLimit,
      rolloverEnabled,
      rolloverBonus,
      spent,
      pct,
      limitPct,
      overPct,
      overage,
      over,
      near: !over && effectiveLimit > 0 && pct >= 80,
    };
  });
}

const CADENCE_LABEL = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  monthly: 'Monthly',
  semiannual: 'Semi-annual',
  yearly: 'Yearly',
};

const MONTHLY_MULT = { daily: 30, weekly: 4, biweekly: 2, monthly: 1, semiannual: 1 / 6, yearly: 1 / 12 };

// Display name for a recurring series: the user's custom rename wins, then
// the auto-normalized merchant name, then the raw bank description.
//
// `legacyKey` is the pre-seriesDescKey identity of the same series. Renames are
// stored per key, so without it a series the user named before reference tokens
// stopped counting toward identity would quietly revert to its bank descriptor.
export function seriesDisplayName(seriesKey, description, seriesNames = {}, legacyKey = null) {
  return (
    seriesNames[firebaseSafeKey(seriesKey)] ||
    (legacyKey ? seriesNames[firebaseSafeKey(legacyKey)] : null) ||
    formatMerchantName(description) ||
    description
  );
}

export function buildRecurringSeries(transactions, { seriesNames = {} } = {}) {
  const seriesMap = {};
  transactions
    .filter((t) => t.isRecurring)
    .forEach((t) => {
      const key = seriesKeyOf(t);
      if (!seriesMap[key]) {
        seriesMap[key] = {
          key,
          id: key,
          // desc is the legible display name; the raw bank description stays
          // on `description` (and on instances) — series keys are derived from
          // the raw value, so it must survive renames and normalization.
          desc: seriesDisplayName(key, t.description, seriesNames, legacySeriesKeyOf(t)),
          description: t.description,
          cat: t.category,
          type: t.type,
          cadence: CADENCE_LABEL[t.recurrenceType] || 'Monthly',
          recurrenceType: t.recurrenceType || 'monthly',
          instances: [],
        };
      }
      seriesMap[key].instances.push(t);
    });

  return Object.values(seriesMap).map((s) => {
    const sorted = [...s.instances].sort((a, b) => new Date(b.date) - new Date(a.date));
    const last = new Date(sorted[0].date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Advance from the last logged instance until the next occurrence is today
    // or later. Without this, a series that hasn't posted in a while reports a
    // "next" date in the past, which drops it from buildUpcoming's forward-
    // looking filter and makes it look like it's not being tracked.
    let next = nextOccurrence(last, s.recurrenceType);
    let safety = 0;
    while (next < today && safety < 10000) {
      next = nextOccurrence(next, s.recurrenceType);
      safety++;
    }
    // amt is the representative (mean) amount across instances; amounts in a
    // series can vary (utilities, usage bills). amtVaries flags the UI.
    const amts = s.instances.map((t) => Number(t.amount) || 0);
    const amt = amts.reduce((a, b) => a + b, 0) / amts.length;
    const amtVaries = Math.max(...amts) - Math.min(...amts) > 0.01;
    // Carry a merchant logo/website from whichever instance has one, so the
    // series (and the upcoming/pending items spread from it) can show it.
    const logoUrl = sorted.find((t) => t.logoUrl)?.logoUrl || null;
    const website = sorted.find((t) => t.website)?.website || null;
    return { ...s, amt, amtVaries, next, when: relDay(next), logoUrl, website };
  });
}

// Planned series as rows shaped like buildRecurringSeries' output, so the Plan
// tabs can list them beside the detected ones. `instances` is empty — nothing
// has posted — and `planned` tells the views to show it as expected, not seen.
export function buildPlannedSeries(planned = [], today = new Date()) {
  return activePlans(planned).map((p) => {
    const { next, overdue } = plannedNext(p, today);
    const key = plannedKeyOf(p);
    return {
      key,
      id: key,
      desc: p.name,
      description: p.name,
      cat: p.category,
      type: p.type,
      cadence: CADENCE_LABEL[p.recurrenceType] || 'Monthly',
      recurrenceType: p.recurrenceType,
      instances: [],
      amt: Number(p.amount),
      amtVaries: false,
      next,
      when: relDay(next),
      logoUrl: null,
      website: null,
      planned: true,
      overdue,
      plan: p,
    };
  });
}

// Recurring bills expected in the given month that haven't been logged yet.
// expandRecurring only emits occurrences after a series' last logged instance,
// so anything it returns inside the month is by definition still pending.
export function buildPendingForMonth(recurring, month) {
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  monthEnd.setHours(23, 59, 59, 999);
  const out = [];
  (recurring || []).forEach((r) => {
    if (!r.instances || r.instances.length === 0) return;
    const lastDate = r.instances.reduce((max, t) => {
      const d = new Date(t.date);
      return d > max ? d : max;
    }, new Date(0));
    const occurrences = expandRecurring(
      { lastDate, recurrenceType: r.recurrenceType },
      monthStart,
      monthEnd,
    );
    occurrences.forEach((date, i) => {
      out.push({
        ...r,
        id: `${r.key}-pending-${i}`,
        next: date,
        when: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      });
    });
  });
  return out.sort((a, b) => a.next - b.next);
}

export function monthlyRecurringNet(recurring) {
  return recurring.reduce((s, r) => {
    const mult = MONTHLY_MULT[r.recurrenceType] || 1;
    const monthly = r.amt * mult;
    return s + (isMoneyIn(r.type) ? monthly : -monthly);
  }, 0);
}

export function adaptProjection(projection) {
  return projection.map((p) => ({
    label: p.label.split(' ')[0] || monthName(new Date()),
    balance: p.balance,
  }));
}

// ── Today (homepage) ─────────────────────────────────────────────────────
//
// One question: "can I spend right now?" The answer is the cash balance minus
// every bill that lands before the next income deposit, so the two lists on
// the page are literally the working for the headline figure.
//
// The bills are read off the runway ledger's own events rather than rebuilt
// from `recurring`, so the hero figure, the row list and the chart agree by
// construction — buildPendingForMonth expands the same series a second time
// and would drift from the chart the moment either projector changed.

// A refund is a purchase coming undone, not money earned — it must never be
// mistaken for a payday. Everything else money-in is a candidate.
//
// A planned deposit (a new job's first paycheque) still moves the runway line,
// but never ends the bill window: it hasn't happened yet, and treating it as
// payday would let safe-to-spend read high on a promise.
const isDeposit = (event) => event.delta > 0 && event.type !== 'refund' && !event.planned;

// ...but being money-in isn't enough to END the bill window. Taking the first
// positive event meant a $20 interest credit on the 3rd closed the window
// before rent on the 5th: the bills after it dropped out of billsTotal and
// safe-to-spend read high — the one direction this figure must never err in.
// So a deposit only counts as "the next deposit" if it is at least half the
// largest one on the horizon, which keeps real paycheques (including the
// smaller of two in a two-earner household) and drops the noise.
const MATERIAL_DEPOSIT_SHARE = 0.5;

function endOfMonth(date) {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Safe-to-spend and the outflows behind it.
 *
 * @param {object} runway  assessRunway() result — `days[]` with `events`.
 * @param {object} options `balance` (cash), `cards` (for statement sub-labels).
 * @returns {null | {
 *   safeToSpend: number, billsTotal: number,
 *   deposit: null | {date: Date, amount: number, description: string},
 *   horizonDate: Date, rows: Array,
 * }}
 */
export function buildTodayOutlook(runway, { balance = 0, cards = [] } = {}) {
  const days = runway?.days;
  if (!days || days.length === 0) return null;

  // Index 0 is today's anchor and never carries events (the balance already
  // reflects everything posted), so the scan starts at the first future day.
  const future = days.slice(1);
  const largestDeposit = future.reduce(
    (max, day) => (day.events || []).reduce(
      (m, e) => (isDeposit(e) && e.delta > m ? e.delta : m),
      max,
    ),
    0,
  );
  const materialFloor = largestDeposit * MATERIAL_DEPOSIT_SHARE;

  let depositIdx = -1;
  let deposit = null;
  for (let i = 1; i < days.length; i += 1) {
    const inc = (days[i].events || [])
      .filter((e) => isDeposit(e) && e.delta >= materialFloor)
      .sort((a, b) => b.delta - a.delta)[0];
    if (inc) {
      depositIdx = i;
      deposit = { date: days[i].date, amount: inc.delta, description: inc.description };
      break;
    }
  }

  // No income found on the horizon — fall back to the end of the month. The
  // caption says so, because "before your next deposit" would be a claim we
  // can't support.
  const horizonDate = deposit ? deposit.date : endOfMonth(days[0].date);
  const lastIdx = depositIdx >= 0
    ? depositIdx
    : days.reduce((last, d, i) => (d.date <= horizonDate ? i : last), 0);

  // Bills landing ON the deposit day are included. Nothing tells us whether
  // rent clears before or after the paycheque posts that morning, so they are
  // counted against you — safe-to-spend errs low, never high.
  const cardById = Object.fromEntries(cards.map((c) => [c.id, c]));
  const rows = days
    .slice(1, lastIdx + 1)
    .flatMap((day) => (day.events || [])
      .filter((e) => e.delta < 0)
      .map((e, n) => {
        const card = e.isCardPayment ? cardById[e.cardAccountId] : null;
        return {
          id: `${day.key}-${e.seriesKey || e.description}-${n}`,
          date: day.date,
          label: e.description,
          // The whole credit-card card from the old dashboard reduces to this:
          // the minimum due is the amount, the statement balance the aside.
          subLabel: card ? ` · statement ${fmt(card.owed, { cents: false })}` : '',
          amount: Math.abs(e.delta),
          income: false,
          planned: !!e.planned,
          missed: !!e.missed,
          event: e,
        };
      }));
  // Rounded once, then reused — deriving safeToSpend from the unrounded sum
  // let the two figures on screen disagree by a cent.
  const billsTotal = round2(rows.reduce((sum, r) => sum + r.amount, 0));

  if (deposit) {
    rows.push({
      id: `${dayLabel(deposit.date)}-deposit`,
      date: deposit.date,
      label: deposit.description,
      subLabel: '',
      amount: deposit.amount,
      income: true,
      event: null,
    });
  }

  return {
    // The page leads with the balance because it is the only figure on it the
    // bank actually reported. Safe-to-spend is ours — a subtraction we chose
    // the terms of — so it reads underneath, sized as the second figure.
    balance: round2(balance),
    safeToSpend: round2(balance - billsTotal),
    billsTotal,
    deposit,
    horizonDate,
    rows,
  };
}

/**
 * Remaining dollars per budgeted category. No bars and no "near limit" amber:
 * the remaining figure is the whole signal, and only the over rows carry
 * colour. Over-budget first, then by remaining ascending.
 */
export function buildLeftToSpend(budgetRows = []) {
  return budgetRows
    .filter((r) => r.effectiveLimit > 0)
    .map((r) => ({
      cat: r.cat,
      limit: r.effectiveLimit,
      remaining: round2(r.effectiveLimit - r.spent),
      over: r.spent > r.effectiveLimit,
    }))
    .sort((a, b) => (a.over === b.over ? a.remaining - b.remaining : a.over ? -1 : 1));
}
